// src/components/notifications/NotificationToast.jsx - Individual Notification Component
import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * NotificationToast - Individual notification card
 *
 * Features:
 * - Automatic animations (slide in/out)
 * - Type-based styling (success/error/warning/info)
 * - Manual dismiss button
 * - Hover to pause auto-dismiss
 * - Shadcn design system styling
 */
const NotificationToast = ({ notification, onDismiss, onHover }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const { id, type, title, message, actions } = notification;

  // Animate in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Handle dismiss with exit animation
  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300); // Match animation duration
  };

  // Icon mapping
  const icons = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };

  const Icon = icons[type] || Info;

  // Type-based styling (matching your existing design system)
  const typeStyles = {
    success: {
      container: 'bg-emerald-900/20 border-emerald-500/30 shadow-emerald-500/10',
      icon: 'text-emerald-400 bg-emerald-500/20',
      text: 'text-emerald-300',
      title: 'text-emerald-200'
    },
    error: {
      container: 'bg-red-900/20 border-red-500/30 shadow-red-500/10',
      icon: 'text-red-400 bg-red-500/20',
      text: 'text-red-300',
      title: 'text-red-200'
    },
    warning: {
      container: 'bg-amber-900/20 border-amber-500/30 shadow-amber-500/10',
      icon: 'text-amber-400 bg-amber-500/20',
      text: 'text-amber-300',
      title: 'text-amber-200'
    },
    info: {
      container: 'bg-blue-900/20 border-blue-500/30 shadow-blue-500/10',
      icon: 'text-blue-400 bg-blue-500/20',
      text: 'text-blue-300',
      title: 'text-blue-200'
    }
  };

  const styles = typeStyles[type] || typeStyles.info;

  return (
    <div
      className={cn(
        // Base styles
        'relative w-full max-w-md p-4 rounded-xl border backdrop-blur-sm',
        'shadow-lg transition-all duration-300 ease-in-out',
        // Type-specific styles
        styles.container,
        // Animation states
        isVisible && !isExiting && 'animate-in slide-in-from-right-full',
        isExiting && 'animate-out slide-out-to-right-full',
        // Hover effect
        'hover:scale-[1.02] hover:shadow-xl'
      )}
      onMouseEnter={() => onHover?.(id, true)}
      onMouseLeave={() => onHover?.(id, false)}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none rounded-xl" />

      {/* Content */}
      <div className="relative flex items-start gap-3">
        {/* Icon */}
        <div className={cn('p-2 rounded-lg', styles.icon)}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={cn('font-semibold text-sm mb-1', styles.title)}>
              {title}
            </h4>
          )}
          <p className={cn('text-sm', styles.text)}>
            {message}
          </p>

          {/* Action buttons (if any) */}
          {actions && actions.length > 0 && (
            <div className="flex gap-2 mt-3">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => action.onClick(notification)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-lg',
                    'transition-colors duration-200',
                    'hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20'
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className={cn(
            'flex-shrink-0 p-1 rounded-lg transition-all duration-200',
            'hover:bg-white/10 focus:outline-none',
            'focus:ring-2 focus:ring-white/20',
            styles.text
          )}
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default NotificationToast;
